import { h, Component, Fragment } from 'preact';
import { observer } from 'mobx-preact';
import { Text, withText } from 'preact-i18n';

import localUtil from '../../lib/util';
import GuiList from '../../components/gui/list';
import '../../components/gui/tileList.css';

// Public «Filer» block on the race and race class pages (2026-10-02): one tile-list row per
// file instead of a pile of badge buttons. The row shows a file-type icon
// tile, the name without its extension, and pills for extension, size and —
// for GPX course files — distance and ascent from file.gpxInfo (computed on
// upload; the race detail API only ships these scalars, never the geoJSON).
// Clicking a row opens the file in a new tab; GPX rows also get a «Vis på
// kart» pill button that opens the raceFileMap drawer (fileMapDrawer.js).

const PILL = 'badge rounded-pill me-1';
const PILL_STYLE = 'font-size: 0.7rem; font-weight: 500;';

export function fileExtension(file = {}) {
    const fromExt = `${file.ext || ''}`.replace(/^\./, '');
    if (fromExt) {
        return fromExt.toLowerCase();
    }
    const match = /\.([a-z0-9]+)$/i.exec(`${file.name || ''}`);
    return match ? match[1].toLowerCase() : '';
}

export function fileBaseName(file = {}) {
    const name = `${file.name || ''}`;
    const ext = fileExtension(file);
    if (ext && name.toLowerCase().endsWith(`.${ext}`)) {
        return name.slice(0, name.length - ext.length - 1);
    }
    return name;
}

export function fileIcon(file = {}) {
    const ext = fileExtension(file);
    if (ext === 'gpx' || ext === 'kml' || ext === 'kmz') {
        return 'fa-duotone fa-route';
    }
    return `fa-duotone ${localUtil.resolveFontawesomeFiletype(ext)}`;
}

// Only GPX uploads get a parsed track (post-fileupload.js), and the drawer
// needs the md5 to fetch it.
export function canShowOnMap(file = {}) {
    return fileExtension(file) === 'gpx' && !!file.md5;
}

// gpxInfo.totalDistance is km (geoLib.distance unit), ascent/decent metres.
export function gpxDistanceKm(file = {}) {
    const km = Number(file.gpxInfo?.totalDistance);
    return Number.isFinite(km) && km > 0 ? km : 0;
}

export function gpxAscentM(file = {}) {
    const m = Number(file.gpxInfo?.ascent);
    return Number.isFinite(m) && m > 0 ? Math.round(m) : 0;
}

function formatKm(km) {
    return km >= 10 ? `${Math.round(km)}` : `${Math.round(km * 10) / 10}`.replace('.', ',');
}

function formatMetres(m) {
    return `${m}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

@withText(() => ({
    titleText: <Text id='input.files'>Files</Text>,
    showOnMapText: <Text id='race.show-on-map'>Show on map</Text>,
}))
@observer
class RaceFileList extends Component {
    // GuiList rows need a numeric id; files have none, so index + 1 is used
    // and mapped back here. The s3 link is external, so a new tab.
    openFile = e => {
        const { files = [] } = this.props;
        const { id } = e.target.closest('.box-line').dataset;
        const file = files[parseInt(id, 10) - 1];
        if (file && file.s3Link) {
            window.open(file.s3Link, '_blank', 'noopener,noreferrer');
        }
    }

    // stopPropagation keeps the row's own click (download) from firing too.
    // Pass raceId (race page) or raceClassId (class page); the drawer picks
    // the matching public geojson route. Full-height drawer: the map needs it.
    showOnMap = (e, file) => {
        e.stopPropagation();
        e.preventDefault();
        const { raceId, raceClassId, drawerLevel = 1, stores } = this.props;
        const { appState } = stores;
        const { drawerHeightLarge } = appState;
        appState.openDrawer('raceFileMap', {
            raceId, raceClassId, file, height: drawerHeightLarge,
        }, drawerLevel + 1);
    }

    renderIconTile = file => (
        <div
            class='d-flex justify-content-center align-items-center rounded-3 bg-body-secondary text-body-secondary'
            style='width: 52px; height: 52px; font-size: 1.5rem;'
        >
            <i class={fileIcon(file)} />
        </div>
    )

    renderPills = file => {
        const { showOnMapText } = this.props;
        const ext = fileExtension(file);
        const km = gpxDistanceKm(file);
        const ascent = gpxAscentM(file);
        return (<>
            {ext ? (
                <span class={`${PILL} text-bg-secondary text-uppercase`} style={PILL_STYLE}>{ext}</span>
            ) : ''}
            {file.size > 0 && (
                <span class={`${PILL} text-bg-light`} style={PILL_STYLE}>{localUtil.formatFileSize(file.size)}</span>
            )}
            {km > 0 && (
                <span class={`${PILL} text-bg-light`} style={PILL_STYLE} title='Distanse'>
                    <i class='fa-duotone fa-route me-1' />{formatKm(km)} km
                </span>
            )}
            {ascent > 0 && (
                <span class={`${PILL} text-bg-light`} style={PILL_STYLE} title='Stigning'>
                    <i class='fa-duotone fa-arrow-trend-up me-1' />{formatMetres(ascent)} m
                </span>
            )}
            {canShowOnMap(file) && (
                <button
                    type='button'
                    class={`${PILL} text-bg-primary border-0`}
                    style={PILL_STYLE}
                    onClick={e => this.showOnMap(e, file)}
                >
                    <i class='fa-duotone fa-map-location-dot me-1' />{showOnMapText}
                </button>
            )}
        </>);
    }

    render() {
        const { titleText, files = [], stores, classNames = 'mt-4' } = this.props;
        if (!files.length) {
            return null;
        }
        const { raceStore } = stores;
        const list = files.map((file, idx) => ({ ...file, id: idx + 1 }));
        return (
            <GuiList
                stores={stores}
                holdingStore={raceStore}
                classNames={classNames}
                classNamesList={'mx-3 tile-list pill-list'}
                title={<>{titleText} ({files.length})</>}
                list={list}
                view={this.openFile}
                elTitle={fileBaseName}
                elInfo1={this.renderPills}
                elInfo2={() => ''}
                elInfo3={() => ''}
                elIconFunc={this.renderIconTile}
                iconStyle='width: 52px; min-width: 52px;'
                showCevron={true}
                showEmptyImage={false}
                showFilterBox={false}
                hideEmpty={true}
            />
        );
    }
}

export default RaceFileList;
